import axios, { AxiosRequestConfig } from "axios";
import { persistor, store } from "../store";
import {
  logout,
  setAuth,
  setMaintenance,
} from "../store/reducers/user.reducer";
import EquimetreRequestConfig from "./equimetreRequestConfig";
import { User } from "./user";

var retry = false;

const MAINTENANCE_POLL_MS = 30_000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type MaintenanceRequestConfig = EquimetreRequestConfig & {
  _maintenance?: boolean;
};

const isMaintenanceError = (error: any) =>
  error?.response?.status === 503 &&
  error?.response?.data?.maintenance === true;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URI, // Replace with your API base URL
  headers: {
    // Accept: 'application/json'
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Modify the request config here (e.g., add headers, authentication tokens)
    const accessToken = localStorage.getItem("token");
    // ** If token is present add it to request's Authorization Header
    if (accessToken) {
      if (config.headers)
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);


type AuthRequestConfig = MaintenanceRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refresh_token = localStorage.getItem("refresh_token");
      if (!refresh_token) throw new Error("No refresh token found");

      const res = await User.refreshUserToken(refresh_token);
      const token = res.data.token;

      localStorage.setItem("token", token);
      if (res.data.refresh_token) {
        localStorage.setItem("refresh_token", res.data.refresh_token);
      }
      store.dispatch(setAuth(true));
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      return token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};


axiosInstance.interceptors.response.use(
  (response) => {
    const config = response.config as AuthRequestConfig;

    // A replayed request succeeded: server is back
    if (config._maintenance) {
      store.dispatch(setMaintenance(false));
    }

    if (
      config.useHydraArrayTransform &&
      response.data &&
      response.data["hydra:member"]
    ) {
      response.data = response.data["hydra:member"];
    }

    return response;
  },
  async (error) => {
    const config = error?.config as AuthRequestConfig | undefined;

    // Maintenance: wait, then replay the same request until the server is back
    if (config && isMaintenanceError(error)) {
      store.dispatch(setMaintenance(true));
      config._maintenance = true;

      await sleep(MAINTENANCE_POLL_MS);

      // Request was cancelled while waiting (e.g. component unmounted)
      if (config.signal?.aborted) {
        store.dispatch(setMaintenance(false));
        return Promise.reject(error);
      }

      return axiosInstance.request(config);
    }

    // A replay failed with a different error: server is up, leave maintenance mode
    if (config?._maintenance) {
      store.dispatch(setMaintenance(false));
    }

    // 401: refresh token once, then replay the failed request
    if (error?.response?.status === 401 && config && !config._retry) {
      config._retry = true; // per-request flag, prevents infinite loops

      let token: string;
      try {
        token = await refreshAccessToken();
      } catch {
        store.dispatch(logout());
        window.localStorage.clear();
        persistor.purge();
        return Promise.reject(error);
      }

      config?.headers && (config.headers.Authorization = `Bearer ${token}`);
      return axiosInstance.request(config);
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
